import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkle, Crown, Check, Spinner, ShieldCheck, BookOpen, VideoCamera,
  CalendarBlank, Clock, Star, ArrowRight, Lightning, Lock, Lifebuoy,
  Download, CaretDown, Flame, Diamond, Heart, MagicWand, Phone, GraduationCap,
} from "@phosphor-icons/react";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { applyCurrencyMethodGate, createRazorpayCheckout } from "../lib/razorpayOptions";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const SYLLABUS = [
  { n: "01", title: "Foundations of Vedic Astrology",      hint: "Houses, signs, planets, the language of the sky",       hue: "from-amber-400 to-orange-500" },
  { n: "02", title: "Reading the Birth Chart (D1)",         hint: "Lagna, lords, aspects, dignities — the master chart",   hue: "from-rose-400 to-pink-500" },
  { n: "03", title: "Nakshatras & the 27 Cosmic Doors",     hint: "Padas, deities, ruling planets — the soul layer",       hue: "from-fuchsia-400 to-purple-500" },
  { n: "04", title: "Vimshottari Dasha System",             hint: "Maha, Antar, Pratyantar — timing the unfolding",        hue: "from-violet-400 to-indigo-500" },
  { n: "05", title: "Yogas — Combinations of Destiny",      hint: "Raja, Dhana, Pancha-Mahapurusha — what marks greatness", hue: "from-indigo-400 to-blue-500" },
  { n: "06", title: "Divisional Charts (D9 · D10 · D11)",   hint: "Marriage, career, wealth — surgical specialisation",    hue: "from-sky-400 to-cyan-500" },
  { n: "07", title: "Doshas & Their Remedies",              hint: "Manglik, Kalsarpa, Pitru — diagnosis & resolution",     hue: "from-teal-400 to-emerald-500" },
  { n: "08", title: "Transits & Sade Sati",                 hint: "Saturn's 7.5-year arc and how to live through it",      hue: "from-emerald-400 to-lime-500" },
  { n: "09", title: "Remedial Astrology",                   hint: "Mantras, gems, donations, rudraksha — what actually works", hue: "from-yellow-400 to-amber-500" },
  { n: "10", title: "Predictive Synthesis",                 hint: "Putting it all together to read a real life chart",    hue: "from-orange-400 to-red-500" },
];

const HIGHLIGHTS = [
  {
    icon: Download,
    title: "10 downloadable videos",
    body: "Yours to keep — watch offline, rewind anytime",
    grad: "from-amber-500 via-orange-500 to-rose-500",
    iconBg: "bg-amber-100 text-amber-700",
  },
  {
    icon: Phone,
    title: "60-min doubt-clearing session",
    body: "Private 1:1 with Messenger of Shiva — ask anything about the course",
    grad: "from-fuchsia-500 via-violet-500 to-indigo-600",
    iconBg: "bg-fuchsia-100 text-fuchsia-700",
  },
  {
    icon: Crown,
    title: "Celebrity Database · FREE bonus",
    body: "Lifetime access to 1,500+ verified charts — worth ₹15,000, yours on enrolment",
    grad: "from-emerald-500 via-teal-500 to-cyan-500",
    iconBg: "bg-emerald-100 text-emerald-700",
    badge: "Worth ₹15,000",
  },
  {
    icon: CalendarBlank,
    title: "2027 launch",
    body: "Chapter links + scheduler emailed at launch",
    grad: "from-sky-500 via-cyan-500 to-emerald-500",
    iconBg: "bg-sky-100 text-sky-700",
  },
];

const FAQS = [
  {
    q: "When will I get access to the videos?",
    a: "All 10 downloadable videos plus your personal-session scheduling link are emailed to you in 2027, the moment the curriculum goes live. You keep the videos forever — download, watch offline, revisit any time.",
  },
  {
    q: "How is the 60-minute doubt-clearing session conducted?",
    a: "After the videos are live, you'll receive a private booking link. Pick a slot that suits you. The session is a 1:1 video call with Messenger of Shiva dedicated entirely to clearing your course doubts — re-explain any chapter, walk through difficult concepts, and get every question answered before you start reading real charts on your own.",
  },
  {
    q: "What if I'm a complete beginner?",
    a: "The course is built ground-up. Chapter 1 starts from absolute basics — houses, signs, planets — and assumes no prior knowledge. By chapter 10 you'll be reading real charts confidently.",
  },
  {
    q: "Is the ₹24,000 a one-time fee or a subscription?",
    a: "Strictly one-time. No recurring charges, no upsells. ₹24,000 covers all 10 videos for life, the single 60-minute doubt-clearing session, AND a lifetime unlock of the AstroKuberChat Celebrity Database (worth ₹15,000) — added to your account the moment your payment goes through.",
  },
  {
    q: "How are my payments secured?",
    a: "Payments are processed by Razorpay — an RBI-licensed PCI-DSS Level 1 payment gateway used by Swiggy, Cred, Zomato and 8M+ Indian businesses. AstroKuberChat never sees or stores your card / UPI details.",
  },
];

const HOW_IT_WORKS = [
  { n: 1, title: "Reserve your seat", body: "Sign in, fill the short form, pay ₹24,000 securely via Razorpay.", icon: Crown, color: "amber" },
  { n: 2, title: "Get your confirmation", body: "Instant email + dashboard entry. Receipt and enrollment ID stored permanently.", icon: Check, color: "emerald" },
  { n: 3, title: "Launch in 2027", body: "10 video downloads + personal-session scheduler emailed to you at launch.", icon: Sparkle, color: "violet" },
];

export default function CourseBookingPage() {
  const { user, loading } = useAuth();

  const [reason, setReason] = useState("");
  const [consentJuly, setConsentJuly] = useState(false);
  const [willingToPay, setWillingToPay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  const name = user?.name || "";
  const email = user?.email || "";

  const reasonValid = reason.trim().length >= 10;
  const canSubmit = !!user && reasonValid && consentJuly && willingToPay && !submitting;

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/course-booking";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleSignIn(redirectUrl);
  };

  const handlePay = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded || !window.Razorpay) {
        toast.error("Could not load Razorpay. Please check your connection.");
        setSubmitting(false);
        return;
      }
      const { data: order } = await api.post("/course/create-order", {
        reason: reason.trim(),
        willing_to_pay: willingToPay,
        consent_july: consentJuly,
      });
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "AstroKuberChat · Astrology Course",
        description: "Messenger of Shiva Astrology Course · ₹24,000 (10 downloadable videos + 1 doubt-clearing session + lifetime Celebrity Database access worth ₹15,000)",
        image: "/favicon.ico",
        prefill: order.prefill,
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            const { data: result } = await api.post("/course/verify-payment", {
              enrollment_id: order.enrollment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess({
              enrollment_id: result.enrollment_id,
              payment_id: result.payment_id,
              already_paid: result.already_paid,
            });
            toast.success("Your seat is confirmed. A confirmation email is on its way.");
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
              window.gtag("event", "conversion_event_purchase", {
                value: 24000,
                currency: "INR",
                transaction_id: result.enrollment_id,
                item_category: "astrology_course",
              });
            }
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed. Contact support.");
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      };
      const rzp = createRazorpayCheckout(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setSubmitting(false);
    }
  };

  // ---------------- Success screen ----------------
  if (success) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="course-success-page">
        <Header />
        <main className="flex-1 max-w-xl mx-auto w-full px-4 sm:px-6 py-14 text-center">
          <div className="relative mx-auto w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 animate-pulse" />
            <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
              <Check size={36} weight="bold" className="text-emerald-600" />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Welcome to the path</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">Your seat is confirmed 🪔</h1>
          <p className="text-muted mt-4">
            We&apos;ve recorded your enrollment for the <strong>Messenger of Shiva · Astrology Learning Course</strong>.
            A confirmation email has been sent to <strong>{email}</strong>.
          </p>
          <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-amber-50 via-rose-50 to-fuchsia-50 border border-amber-200 text-left text-sm space-y-2">
            <div className="flex justify-between"><span className="text-muted">Enrollment ID</span><span className="font-mono font-semibold text-[#3F2D1B]" data-testid="enrollment-id">{success.enrollment_id}</span></div>
            {success.payment_id && (
              <div className="flex justify-between"><span className="text-muted">Payment ID</span><span className="font-mono text-[#3F2D1B]">{success.payment_id}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted">Amount</span><span className="font-mono font-semibold text-emerald-700">₹24,000 paid</span></div>
            <div className="flex justify-between"><span className="text-muted">Delivery</span><span className="text-[#3F2D1B]">By January 2027</span></div>
          </div>
          <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-left text-sm">
            <div className="flex items-start gap-2.5">
              <Crown size={18} weight="duotone" className="text-emerald-700 flex-none mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">Bonus unlocked · Celebrity Database (lifetime)</div>
                <div className="text-emerald-800/80 text-xs mt-0.5">
                  All 1,500+ verified charts are now live in your account — worth ₹15,000.{" "}
                  <Link to="/celebrities" className="underline font-medium hover:text-emerald-900">Explore now →</Link>
                </div>
              </div>
            </div>
          </div>
          <p className="text-muted text-xs mt-5">
            Chapter downloadable videos and your personal-session scheduling link will be emailed when the curriculum goes live.
          </p>
          <div className="mt-7 flex justify-center gap-3">
            <Link to="/" className="btn-ghost rounded-full px-5 py-2.5 text-sm">Back to home</Link>
            <Link to="/dashboard" className="btn-primary rounded-full px-5 py-2.5 text-sm" data-testid="go-to-dashboard">Go to dashboard <ArrowRight size={14} className="inline ml-1" /></Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------------- Sign-in gate ----------------
  if (!loading && !user) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="course-signin-gate">
        <Header />
        <main className="flex-1 max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="relative mx-auto w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 blur-sm opacity-70" />
            <div className="relative rounded-full bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 w-20 h-20 flex items-center justify-center">
              <Crown size={36} weight="duotone" className="text-white" />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Astrology Learning Course</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">Sign in to book your seat</h1>
          <p className="text-muted mt-3 max-w-md mx-auto">
            The Messenger of Shiva Astrology Learning Course is reserved for signed-in seekers.
            Sign in with Google to continue to the booking form.
          </p>
          <button
            onClick={handleLogin}
            className="inline-block mt-6 btn-primary rounded-full px-6 py-2.5 text-sm"
            data-testid="course-signin-btn"
          >
            Sign in with Google
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------------- Main booking page ----------------
  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="course-booking-page">
      <Header />
      <main className="flex-1">

        {/* ===== Hero — Aurora gradient panel ===== */}
        <section className="relative overflow-hidden">
          {/* Aurora blobs */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-gradient-to-br from-amber-400/35 via-orange-500/30 to-transparent blur-3xl" />
            <div className="absolute top-[20%] right-[-15%] w-[55%] h-[55%] rounded-full bg-gradient-to-br from-fuchsia-400/30 via-violet-500/25 to-transparent blur-3xl" />
            <div className="absolute bottom-[-20%] left-[20%] w-[60%] h-[60%] rounded-full bg-gradient-to-tr from-sky-400/25 via-cyan-400/20 to-transparent blur-3xl" />
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-14 sm:pb-20 text-center">
            {/* Top live badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-amber-200 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-600"></span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-rose-700 font-semibold">Enrolment paused · Course begins 2027</span>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-700 font-semibold">
              <Sparkle size={14} weight="duotone" /> Messenger of Shiva · Flagship Course
            </div>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 font-light leading-[1.05] tracking-tight">
              Book your ticket to learn{" "}
              <span className="bg-gradient-to-r from-amber-600 via-orange-500 via-rose-500 to-fuchsia-600 bg-clip-text text-transparent">Astrology</span>{" "}
              from <span className="italic font-serif">Messenger of Shiva</span>
            </h1>
            <p className="text-muted mt-6 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
              Ten precisely-built, <span className="font-semibold text-[#3F2D1B]">downloadable</span> chapter videos plus a
              private 60-minute doubt-clearing session with the Messenger himself.{" "}
              <span className="whitespace-nowrap">One-time ₹24,000.</span>{" "}
              <b className="whitespace-nowrap text-rose-700">Course begins 2027 — enrolment opens closer to launch.</b>
            </p>
            <p className="text-emerald-700 mt-3 max-w-2xl mx-auto text-sm font-medium">
              ✨ Plus: lifetime access to the AstroKuberChat Celebrity Database (worth ₹15,000) — unlocked at course launch.
            </p>

            {/* CTA */}
            <div className="mt-8 flex justify-center">
              <a href="#booking-form" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-amber-300 text-[#3F2D1B] text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all">
                See course details <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </section>

        {/* ===== Trust badges strip ===== */}
        <section className="bg-white/70 backdrop-blur border-y border-amber-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 text-center">
            <TrustBadge icon={ShieldCheck} label="RBI-licensed gateway" sub="Razorpay · PCI-DSS L1" color="text-emerald-700" bg="bg-emerald-50" />
            <TrustBadge icon={Lock} label="256-bit TLS secure" sub="End-to-end encrypted" color="text-sky-700" bg="bg-sky-50" />
            <TrustBadge icon={Download} label="Yours to keep" sub="Download · watch offline" color="text-violet-700" bg="bg-violet-50" />
            <TrustBadge icon={Lifebuoy} label="Direct support" sub="Email within 24 hrs" color="text-rose-700" bg="bg-rose-50" />
          </div>
        </section>

        {/* ===== Vibrant Highlight tiles ===== */}
        <section className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {HIGHLIGHTS.map((h) => (
              <div key={h.title} className="group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all hover:-translate-y-1">
                <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${h.grad}`} />
                <div className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className={`w-11 h-11 rounded-xl ${h.iconBg} flex items-center justify-center`}>
                      <h.icon size={22} weight="duotone" />
                    </div>
                    {h.badge && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] uppercase tracking-wider font-bold">
                        {h.badge}
                      </span>
                    )}
                  </div>
                  <div className="font-serif-display text-lg sm:text-xl text-[#3F2D1B] mt-4">{h.title}</div>
                  <div className="text-sm text-muted mt-1.5 leading-relaxed">{h.body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== Instructor card ===== */}
        <section className="max-w-5xl mx-auto w-full px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2A1B0E] via-[#3F2D1B] to-[#5C3A1A] text-white p-6 sm:p-9 shadow-xl">
            <div className="absolute top-0 right-0 w-72 h-72 -mt-12 -mr-12 bg-gradient-to-br from-amber-400/30 to-rose-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 -mb-20 -ml-16 bg-gradient-to-tr from-fuchsia-500/20 to-indigo-500/20 rounded-full blur-3xl" />
            <div className="relative grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-6 items-center">
              <div className="flex justify-center sm:justify-start">
                <div className="relative">
                  <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 blur-md opacity-60" />
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden shadow-2xl ring-4 ring-white/15">
                    <img
                      src="https://customer-assets.emergentagent.com/job_astro-connect-42/artifacts/qyiu3jxv_IMG_4140.jpg"
                      alt="Messenger of Shiva"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      data-testid="instructor-photo"
                    />
                  </div>
                </div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/90 font-semibold">Your teacher</div>
                <h3 className="font-serif-display text-2xl sm:text-3xl mt-1 font-light">Messenger of Shiva</h3>
                <p className="text-amber-50/80 text-sm mt-3 leading-relaxed max-w-xl">
                  Vedic astrologer and founder of AstroKuberChat. Author of the platform&apos;s
                  KP, Navamsa, Rajayoga, Lal Kitab and 20-Year Predictive engines —
                  used live by thousands of seekers every month.
                </p>
                <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2">
                  <Pill icon={GraduationCap}>Vedic · KP · Western</Pill>
                  <Pill icon={Sparkle}>Swiss Ephemeris precision</Pill>
                  <Pill icon={Diamond}>1:1 doubt-clearing access</Pill>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== How it works — 3 steps ===== */}
        <section className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-14">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 font-semibold">Simple, transparent</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">How it works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
            {HOW_IT_WORKS.map((s, i) => (
              <div key={s.n} className="relative">
                {i < HOW_IT_WORKS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] right-[-10%] h-px bg-gradient-to-r from-amber-300 via-rose-300 to-violet-300" />
                )}
                <div className="relative bg-white rounded-2xl p-5 sm:p-6 border border-soft shadow-sm hover:shadow-md transition-all">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${
                    s.color === "amber" ? "from-amber-400 to-orange-500" :
                    s.color === "emerald" ? "from-emerald-400 to-teal-500" :
                    "from-fuchsia-400 to-violet-500"
                  } flex items-center justify-center text-white shadow-lg`}>
                    <s.icon size={26} weight="duotone" />
                  </div>
                  <div className="absolute top-5 right-5 font-serif-display text-5xl font-light text-[#3F2D1B]/10">0{s.n}</div>
                  <h3 className="font-serif-display text-xl mt-4 text-[#3F2D1B]">{s.title}</h3>
                  <p className="text-sm text-muted mt-1.5 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== Syllabus — colourful chapter cards ===== */}
        <section className="max-w-5xl mx-auto w-full px-4 sm:px-6 pb-14">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-amber-200 shadow-sm">
              <BookOpen size={14} weight="duotone" className="text-amber-600" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">10-chapter curriculum</span>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light">What you will learn</h2>
            <p className="text-muted mt-2 max-w-xl mx-auto">From the first house to a fully synthesised reading — built so a complete beginner finishes confident.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {SYLLABUS.map((s) => (
              <div key={s.n} className="group relative overflow-hidden rounded-xl bg-white border border-soft p-4 sm:p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className={`absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b ${s.hue}`} />
                <div className="flex gap-4 items-start pl-2">
                  <div className={`flex-none w-12 h-12 rounded-xl bg-gradient-to-br ${s.hue} text-white font-mono text-sm font-bold flex items-center justify-center shadow-md`}>
                    {s.n}
                  </div>
                  <div>
                    <div className="font-serif-display text-lg text-[#3F2D1B] leading-tight">{s.title}</div>
                    <div className="text-xs text-muted mt-1.5 leading-relaxed">{s.hint}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== Booking form (anchor) ===== */}
        <section id="booking-form" className="max-w-3xl mx-auto w-full px-4 sm:px-6 pb-14 scroll-mt-24">
          <div className="relative">
            {/* Decorative ribbon */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white text-[10px] uppercase tracking-[0.2em] font-semibold shadow-md">
              <Flame size={12} weight="fill" /> Enrolment opens 2027
            </div>
            <div className="bg-white rounded-2xl border border-amber-200 shadow-xl p-6 sm:p-8 pt-9">
              <div className="flex items-center gap-2 text-amber-700">
                <Crown size={18} weight="duotone" />
                <h2 className="font-serif-display text-2xl font-light tracking-wide">Course begins 2027</h2>
              </div>
              <p className="text-muted text-xs mt-1.5">Bookings are paused. Enrolment will reopen closer to the 2027 launch — you&apos;ll get an email from us then.</p>

              {/* Enrolment paused — 2026-08-06.
                  The full form + Razorpay pay flow are commented out
                  below (kept intact in git history so reopening in 2027
                  is a code-revert, no rebuild required). Users see a
                  clear "course begins 2027" notice instead of a broken
                  pay button. */}
              <div className="mt-6 rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-6 text-center" data-testid="course-enrolment-paused">
                <div className="mx-auto w-12 h-12 rounded-full bg-white border border-amber-300 flex items-center justify-center shadow-sm">
                  <Crown size={22} weight="duotone" className="text-amber-600" />
                </div>
                <div className="mt-4 font-serif-display text-2xl text-[#3F2D1B]">Course begins 2027</div>
                <p className="text-sm text-[#3F2D1B]/80 mt-2 leading-relaxed max-w-md mx-auto">
                  We&apos;re polishing the final chapters with the Messenger of Shiva.
                  Enrolment for the ₹24,000 flagship course will reopen closer to the 2027 launch.
                </p>
                <p className="text-xs text-muted mt-3 max-w-md mx-auto">
                  For updates, keep in touch via the AstroKuberChat app / email — you&apos;ll be the first to know when we open seats.
                </p>
                <button
                  type="button"
                  disabled
                  className="mt-5 rounded-full px-6 py-3 text-sm font-semibold bg-stone-200 text-stone-500 cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="course-pay-btn"
                  aria-disabled="true"
                  title="Enrolment paused — reopens 2027"
                >
                  <Lock size={14} weight="bold" /> Enrolment paused · reopens 2027
                </button>
              </div>

              {/* eslint-disable */}
              {false && (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="label-eyebrow">Your name</label>
                  <input
                    type="text"
                    value={name}
                    readOnly
                    className="mt-1.5 w-full px-3 py-2.5 border border-soft rounded-md bg-amber-50/60 text-sm text-[#3F2D1B]"
                    data-testid="course-name-input"
                  />
                </div>
                <div>
                  <label className="label-eyebrow">Your email</label>
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="mt-1.5 w-full px-3 py-2.5 border border-soft rounded-md bg-amber-50/60 text-sm text-[#3F2D1B]"
                    data-testid="course-email-input"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="label-eyebrow">Why do you want to enrol in this course?</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={1000}
                  rows={4}
                  placeholder="Tell us in 2–3 sentences — your current understanding of astrology, what drew you to this course, and what you hope to learn."
                  className="mt-1.5 w-full px-3 py-2.5 border border-soft rounded-md focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none text-sm leading-relaxed transition-all"
                  data-testid="course-reason-input"
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-[11px] ${reasonValid ? "text-emerald-700 font-medium" : "text-muted"}`}>
                    {reasonValid ? "✓ Looks good." : "At least 10 characters."}
                  </span>
                  <span className="text-[11px] text-muted font-mono">{reason.length}/1000</span>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <ConsentCheckbox
                  checked={consentJuly}
                  onChange={setConsentJuly}
                  testId="consent-july"
                  label="I understand the course curriculum will be delivered by January 2027 and I am OK to wait."
                />
                <ConsentCheckbox
                  checked={willingToPay}
                  onChange={setWillingToPay}
                  testId="consent-pay"
                  label="I am willing to pay the one-time fee of ₹24,000 (twenty-four thousand rupees) to reserve my seat."
                />
              </div>

              <div className="mt-6 rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-4 text-xs space-y-1.5">
                <div className="flex justify-between"><span className="text-muted">Course fee</span><span className="font-mono font-semibold text-[#3F2D1B]">₹24,000</span></div>
                <div className="flex justify-between"><span className="text-muted">Delivery</span><span className="text-[#3F2D1B]">By January 2027</span></div>
                <div className="flex justify-between"><span className="text-muted">Includes</span><span className="text-[#3F2D1B]">10 downloadable videos + 1 doubt-clearing session</span></div>
                <div className="flex justify-between"><span className="text-emerald-700 font-medium">Bonus (free)</span><span className="text-emerald-700 font-medium">Celebrity DB · lifetime · worth ₹15,000</span></div>
                <div className="flex justify-between pt-1.5 border-t border-amber-200/60 font-semibold">
                  <span className="text-amber-700">Total today</span>
                  <span className="font-mono text-[#3F2D1B]">₹24,000</span>
                </div>
              </div>

              <button
                onClick={handlePay}
                disabled={!canSubmit}
                className="group w-full mt-6 rounded-full px-5 py-4 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow-lg shadow-orange-200/70 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:hover:transform-none disabled:shadow-none"
                data-testid="course-pay-btn"
              >
                {submitting ? (
                  <><Spinner size={14} className="animate-spin" /> Processing payment…</>
                ) : (
                  <>
                    <Lock size={14} weight="bold" />
                    Pay ₹24,000 securely & confirm my seat
                    <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>

              <div className="mt-4 flex items-center justify-center gap-4 text-[10px] text-muted">
                <span className="flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-700" /> Razorpay</span>
                <span className="flex items-center gap-1"><Lock size={11} className="text-sky-700" /> 256-bit TLS</span>
                <span className="flex items-center gap-1"><Heart size={11} className="text-rose-600" /> No upsells</span>
              </div>
              <p className="text-[10px] text-muted text-center mt-2">AstroKuberChat never sees your card or UPI details.</p>
              </>
              )}
              {/* eslint-enable */}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="max-w-3xl mx-auto w-full px-4 sm:px-6 pb-16">
          <div className="text-center mb-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 font-semibold">Common questions</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">Before you book</h2>
          </div>
          <div className="space-y-2.5">
            {FAQS.map((f, i) => (
              <button
                key={f.q}
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className={`w-full text-left rounded-xl border transition-all overflow-hidden ${openFaq === i ? "bg-white border-amber-300 shadow-md" : "bg-white/70 border-soft hover:border-amber-200"}`}
                data-testid={`faq-${i}`}
              >
                <div className="flex items-start justify-between gap-3 px-5 py-4">
                  <span className="font-medium text-sm sm:text-base text-[#3F2D1B] leading-snug">{f.q}</span>
                  <CaretDown size={16} weight="bold" className={`flex-none mt-1 text-amber-600 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </div>
                {openFaq === i && (
                  <div className="px-5 pb-4 -mt-1 text-sm text-muted leading-relaxed border-t border-amber-100/60 pt-3">
                    {f.a}
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* ===== Final CTA strip ===== */}
        <section className="max-w-5xl mx-auto w-full px-4 sm:px-6 pb-16">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 text-white p-7 sm:p-10 text-center shadow-2xl">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 left-1/4 w-40 h-40 bg-white/30 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-fuchsia-400/40 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <MagicWand size={28} weight="duotone" className="mx-auto mb-3 text-amber-100" />
              <h3 className="font-serif-display text-2xl sm:text-3xl font-light">This course launches in 2027</h3>
              <p className="text-amber-50/90 text-sm mt-2 max-w-xl mx-auto">
                Enrolment for the Messenger of Shiva flagship course is paused while we polish the final chapters.
                Booking will reopen closer to the 2027 launch — thanks for your patience.
              </p>
              <div className="inline-flex items-center gap-2 mt-5 px-7 py-3 rounded-full bg-white/95 text-[#3F2D1B] font-semibold text-sm shadow-lg" data-testid="course-cta-bottom">
                <Lock size={14} weight="bold" /> Enrolment reopens 2027
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}

function TrustBadge({ icon: Icon, label, sub, color, bg }) {
  return (
    <div className="flex items-center gap-3 justify-center sm:justify-start">
      <div className={`w-9 h-9 rounded-lg ${bg} ${color} flex items-center justify-center flex-none`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="text-left">
        <div className="text-xs font-semibold text-[#3F2D1B] leading-tight">{label}</div>
        <div className="text-[10px] text-muted leading-tight mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-amber-50 text-[11px] backdrop-blur">
      <Icon size={12} weight="duotone" /> {children}
    </span>
  );
}

function ConsentCheckbox({ checked, onChange, label, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-lg border border-soft hover:border-amber-300 hover:bg-amber-50/50 transition-all">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer flex-none"
        data-testid={testId}
      />
      <span className="text-sm leading-snug text-[#3F2D1B] group-hover:text-[#1d130a]">{label}</span>
    </label>
  );
}
